const DISPLAY_PAD_LENGTH = 2;
const MONTHS_IN_YEAR = 12;

function stripTime(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function isValidDate(value: Date | null | undefined): value is Date {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

function normalizeDateValue(value: unknown): Date | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (value instanceof Date) {
    return isValidDate(value) ? value : null;
  }

  if (typeof value === 'number') {
    if (!Number.isFinite(value)) {
      return null;
    }

    const date = new Date(value);

    return isValidDate(date) ? date : null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();

    if (trimmed === '') {
      return null;
    }

    const date = new Date(trimmed);

    return isValidDate(date) ? date : null;
  }

  return null;
}

function clampDate(date: Date, minDate?: Date, maxDate?: Date) {
  const normalizedDate = stripTime(date);
  const normalizedMinDate = isValidDate(minDate) ? stripTime(minDate) : null;
  const normalizedMaxDate = isValidDate(maxDate) ? stripTime(maxDate) : null;

  if (normalizedMinDate && normalizedDate < normalizedMinDate) {
    return normalizedMinDate;
  }

  if (normalizedMaxDate && normalizedDate > normalizedMaxDate) {
    return normalizedMaxDate;
  }

  return normalizedDate;
}

function resolveInitialDate(params: {
  value?: Date | null;
  defaultValue?: Date | null;
  minDate?: Date;
  maxDate?: Date;
}) {
  const {value, defaultValue, minDate, maxDate} = params;
  const fallbackDate = [value, defaultValue].find(isValidDate) ?? new Date();

  return clampDate(fallbackDate, minDate, maxDate);
}

function getMonthLabels(locale = 'en-US') {
  const formatter = new Intl.DateTimeFormat(locale, {month: 'long'});

  return Array.from({length: MONTHS_IN_YEAR}, (_, monthIndex) =>
    formatter.format(new Date(2024, monthIndex, 1)),
  );
}

function getDaysInMonth(year: number, monthIndex: number) {
  return new Date(year, monthIndex + 1, 0).getDate();
}

function getYearRange(minDate?: Date, maxDate?: Date) {
  const normalizedMin = isValidDate(minDate) ? stripTime(minDate) : null;
  const normalizedMax = isValidDate(maxDate) ? stripTime(maxDate) : null;
  const today = new Date();
  const startYear = normalizedMin?.getFullYear() ?? 1900;
  const endYear = normalizedMax?.getFullYear() ?? today.getFullYear() + 20;
  const fromYear = Math.min(startYear, endYear);
  const toYear = Math.max(startYear, endYear);

  return Array.from({length: toYear - fromYear + 1}, (_, index) => fromYear + index);
}

function buildDateFromParts(year: number, monthIndex: number, day: number) {
  const normalizedDay = Math.min(day, getDaysInMonth(year, monthIndex));

  return new Date(year, monthIndex, normalizedDay);
}

function formatDateValue(date: Date) {
  const day = String(date.getDate()).padStart(DISPLAY_PAD_LENGTH, '0');
  const month = String(date.getMonth() + 1).padStart(DISPLAY_PAD_LENGTH, '0');
  const year = String(date.getFullYear());

  return `${day}/${month}/${year}`;
}

function formatDateInputValue(date: Date) {
  const day = String(date.getDate()).padStart(DISPLAY_PAD_LENGTH, '0');
  const month = String(date.getMonth() + 1).padStart(DISPLAY_PAD_LENGTH, '0');
  const year = String(date.getFullYear());

  return `${year}-${month}-${day}`;
}

function getWheelOptionStyles(index: number, scrollTop: number, optionHeight: number) {
  const virtualCenterIndex = scrollTop / optionHeight;
  const offsetFromCenter = index - virtualCenterIndex;
  const distance = Math.abs(offsetFromCenter);
  const normalizedDistance = Math.min(distance / 3.5, 1);
  const centerInfluence = 1 - normalizedDistance;
  const easedInfluence = 1 - normalizedDistance ** 1.65;
  const rotation = Math.max(-42, Math.min(42, offsetFromCenter * 11));
  const scale = 0.875 + easedInfluence * 0.125;
  const scaleY = 0.66 + easedInfluence * 0.34;
  const translateY = offsetFromCenter * 0.9;
  const translateZ = Math.max(0, 14 - distance * 4.5);
  const blur = (1 - easedInfluence) * 1.2;
  const opacity = 0.12 + easedInfluence * 0.88;
  const fontSize = 16 + easedInfluence * 6;
  const isSelected = distance < 0.5;
  const textShade = isSelected
    ? Math.round(164 - centerInfluence * 117)
    : Math.round(180 - easedInfluence * 60);

  return {
    distance,
    style: {
      height: `${optionHeight}px`,
      lineHeight: `${optionHeight}px`,
      fontSize: `${fontSize}px`,
      color: `rgb(${textShade}, ${textShade}, ${textShade})`,
      opacity,
      transform: `perspective(1400px) translate3d(0, ${translateY}px, ${translateZ}px) rotateX(${rotation}deg) scale(${scale}, ${scaleY})`,
      transformOrigin: 'center center',
      filter: `blur(${blur}px)`,
      willChange: 'transform, opacity, filter',
    },
  };
}

export {
  buildDateFromParts,
  clampDate,
  formatDateInputValue,
  formatDateValue,
  getDaysInMonth,
  getMonthLabels,
  getWheelOptionStyles,
  getYearRange,
  isValidDate,
  normalizeDateValue,
  resolveInitialDate,
  stripTime,
};
